import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * Pan + zoom cropper that exports a square JPEG blob (default 512px).
 * Preview size adapts to the available width (mobile-friendly).
 */
const ImageSquareCropper = ({
    file,
    outputSize = 512,
    onCancel,
    onConfirm,
    confirmLabel = "Зберегти квадрат",
}) => {
    const canvasRef = useRef(null);
    const stageRef = useRef(null);
    const imgRef = useRef(null);
    const dragRef = useRef(null);
    const boxSizeRef = useRef(240);
    const [boxSize, setBoxSize] = useState(240);
    const [ready, setReady] = useState(false);
    const [scale, setScale] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [minScale, setMinScale] = useState(1);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const objectUrl = useMemo(() => {
        if (!file) return "";
        return URL.createObjectURL(file);
    }, [file]);

    useEffect(() => () => {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
    }, [objectUrl]);

    useEffect(() => {
        const el = stageRef.current;
        if (!el || typeof ResizeObserver === "undefined") {
            const fallback = Math.max(180, Math.min(280, (window.innerWidth || 360) - 48));
            boxSizeRef.current = fallback;
            setBoxSize(fallback);
            return undefined;
        }
        const apply = () => {
            const w = Math.floor(el.clientWidth || 240);
            const next = Math.max(180, Math.min(280, w));
            boxSizeRef.current = next;
            setBoxSize(next);
        };
        apply();
        const ro = new ResizeObserver(apply);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const fitImageToBox = (img, size) => {
        const cover = Math.max(size / img.width, size / img.height);
        setMinScale(cover);
        setScale(cover);
        setOffset({
            x: (size - img.width * cover) / 2,
            y: (size - img.height * cover) / 2,
        });
    };

    useEffect(() => {
        if (!objectUrl) return undefined;
        setReady(false);
        setError("");
        const img = new Image();
        img.onload = () => {
            imgRef.current = img;
            fitImageToBox(img, boxSizeRef.current);
            setReady(true);
        };
        img.onerror = () => setError("Не вдалося прочитати зображення");
        img.src = objectUrl;
        return undefined;
    }, [objectUrl]);

    useEffect(() => {
        if (!ready || !imgRef.current) return;
        fitImageToBox(imgRef.current, boxSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [boxSize]);

    useEffect(() => {
        if (!ready || !imgRef.current || !canvasRef.current) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        const box = canvas.width;
        ctx.clearRect(0, 0, box, box);
        ctx.fillStyle = "#0f172a";
        ctx.fillRect(0, 0, box, box);
        ctx.drawImage(
            imgRef.current,
            offset.x,
            offset.y,
            imgRef.current.width * scale,
            imgRef.current.height * scale,
        );
        ctx.strokeStyle = "rgba(255,255,255,0.85)";
        ctx.lineWidth = 2;
        ctx.strokeRect(1, 1, box - 2, box - 2);
    }, [ready, scale, offset, boxSize]);

    const clampOffset = (nextScale, nextOffset, size = boxSizeRef.current) => {
        const img = imgRef.current;
        if (!img) return nextOffset;
        const w = img.width * nextScale;
        const h = img.height * nextScale;
        let x = nextOffset.x;
        let y = nextOffset.y;
        if (w <= size) x = (size - w) / 2;
        else x = Math.min(0, Math.max(size - w, x));
        if (h <= size) y = (size - h) / 2;
        else y = Math.min(0, Math.max(size - h, y));
        return { x, y };
    };

    const onPointerDown = (e) => {
        e.preventDefault();
        const point = e.touches ? e.touches[0] : e;
        dragRef.current = {
            startX: point.clientX,
            startY: point.clientY,
            origin: { ...offset },
        };
    };

    const onPointerMove = (e) => {
        if (!dragRef.current) return;
        e.preventDefault();
        const point = e.touches ? e.touches[0] : e;
        const dx = point.clientX - dragRef.current.startX;
        const dy = point.clientY - dragRef.current.startY;
        setOffset(clampOffset(scale, {
            x: dragRef.current.origin.x + dx,
            y: dragRef.current.origin.y + dy,
        }));
    };

    const onPointerUp = () => {
        dragRef.current = null;
    };

    const onScaleChange = (value) => {
        const next = Math.max(minScale, Number(value) || minScale);
        setScale(next);
        setOffset((prev) => clampOffset(next, prev));
    };

    const exportCrop = async () => {
        const img = imgRef.current;
        if (!img || busy) return;
        setBusy(true);
        setError("");
        try {
            const out = document.createElement("canvas");
            out.width = outputSize;
            out.height = outputSize;
            const ctx = out.getContext("2d");
            const previewBox = boxSizeRef.current || boxSize;
            const ratio = outputSize / previewBox;
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, outputSize, outputSize);
            ctx.drawImage(
                img,
                offset.x * ratio,
                offset.y * ratio,
                img.width * scale * ratio,
                img.height * scale * ratio,
            );
            const blob = await new Promise((resolve) => out.toBlob(resolve, "image/jpeg", 0.9));
            if (!blob) {
                setError("Не вдалося підготувати файл");
                return;
            }
            const cropped = new File([blob], "network-logo.jpg", { type: "image/jpeg" });
            await onConfirm?.(cropped);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="logo-cropper">
            <p className="logo-cropper__hint">
                Перетягніть фото, щоб обрати фрагмент. Масштаб — повзунком. У клієнта буде квадрат.
            </p>
            <div
                ref={stageRef}
                className="logo-cropper__stage"
                onMouseDown={onPointerDown}
                onMouseMove={onPointerMove}
                onMouseUp={onPointerUp}
                onMouseLeave={onPointerUp}
                onTouchStart={onPointerDown}
                onTouchMove={onPointerMove}
                onTouchEnd={onPointerUp}
            >
                <canvas ref={canvasRef} width={boxSize} height={boxSize} />
            </div>
            <label className="logo-cropper__zoom">
                <span>Масштаб</span>
                <input
                    type="range"
                    min={minScale}
                    max={Math.max(minScale * 3, minScale + 0.01)}
                    step={0.01}
                    value={scale}
                    onChange={(e) => onScaleChange(e.target.value)}
                    disabled={!ready || busy}
                />
            </label>
            {error ? <p className="logo-cropper__error">{error}</p> : null}
            <div className="logo-cropper__actions">
                <button
                    type="button"
                    className="add-btn"
                    onClick={exportCrop}
                    disabled={!ready || busy}
                >
                    {busy ? "Збереження..." : confirmLabel}
                </button>
                <button
                    type="button"
                    className="add-btn add-btn--secondary"
                    onClick={onCancel}
                    disabled={busy}
                >
                    Скасувати
                </button>
            </div>
        </div>
    );
};

export default ImageSquareCropper;
